import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../utils/apiClient";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const DataTable = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [addLoading, setAddLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newJob, setNewJob] = useState({
    englishTitle: "",
    chineseTitle: "",
    englishDescription: "",
    chineseDescription: "",
    image: "",
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState({
    id: null,
    englishTitle: "",
    chineseTitle: "",
    englishDescription: "",
    chineseDescription: "",
    image: "",
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRefAdd = useRef(null);
  const fileInputRefEdit = useRef(null);

  const fetchJobs = async () => {
    try {
      const response = await apiClient.get("/jobs/get-all");
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const uploadImage = async (file, forEdit = false) => {
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await apiClient.post("/shared/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const imageUrl = response.data?.imageUrl;

      if (imageUrl) {
        if (forEdit) {
          setEditingJob((prev) => ({
            ...prev,
            image: imageUrl,
          }));
        } else {
          setNewJob((prev) => ({
            ...prev,
            image: imageUrl,
          }));
        }
      } else {
        toast.error("No image URL returned from server");
      }
    } catch (err) {
      toast.error("Image upload failed");
      console.error(err);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUploadImage = (forEdit = false) => {
    const fileInput = forEdit ? fileInputRefEdit.current : fileInputRefAdd.current;
    fileInput.click();
  };

  const handleFileChange = async (e, forEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      await uploadImage(file, forEdit);
    }
    e.target.value = null; // Reset file input
  };

  const handleRemoveImage = (forEdit = false) => {
    if (forEdit) {
      setEditingJob((prev) => ({
        ...prev,
        image: "",
      }));
    } else {
      setNewJob((prev) => ({
        ...prev,
        image: "",
      }));
    }
  };

  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setNewJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingJob((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setNewJob({
      englishTitle: "",
      chineseTitle: "",
      englishDescription: "",
      chineseDescription: "",
      image: "",
    });
    setAddModalOpen(true);
  };

  const closeAllModals = () => {
    setAddModalOpen(false);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
    setDeletingJobId(null);
  };

  const openEditModal = (job) => {
    setEditingJob({
      id: job.id,
      englishTitle: job.englishTitle,
      chineseTitle: job.chineseTitle,
      englishDescription: job.englishDescription,
      chineseDescription: job.chineseDescription,
      image: job.image,
    });
    setEditModalOpen(true);
  };

  const openDeleteModal = (id) => {
    setDeletingJobId(id);
    setDeleteModalOpen(true);
  };

  const handleAddJob = async () => {
    const { englishTitle, chineseTitle, englishDescription, chineseDescription, image } = newJob;

    if (!englishTitle || !chineseTitle || !englishDescription || !chineseDescription || !image) {
      toast.error("Please fill all fields and upload an image");
      return;
    }

    setAddLoading(true);
    try {
      await apiClient.post("/jobs/add", newJob);
      toast.success("Job added successfully!");
      await fetchJobs();
      closeAllModals();
    } catch (err) {
      toast.error("Failed to add job");
      console.error(err);
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateJob = async () => {
    const { id, englishTitle, chineseTitle, englishDescription, chineseDescription, image } = editingJob;

    if (!englishTitle || !chineseTitle || !englishDescription || !chineseDescription || !image) {
      toast.error("Please fill all fields and upload an image");
      return;
    }

    setIsUpdating(true);
    try {
      await apiClient.put(`/jobs/update/${id}`, {
        englishTitle,
        chineseTitle,
        englishDescription,
        chineseDescription,
        image,
      });
      toast.success("Job updated successfully!");
      await fetchJobs();
      closeAllModals();
    } catch (err) {
      toast.error("Failed to update job");
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!deletingJobId) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/jobs/delete/${deletingJobId}`);
      await fetchJobs();
      toast.success("Job deleted successfully!");
      closeAllModals();
    } catch (err) {
      toast.error("Failed to delete job");
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const quillStyle = { minHeight: "150px" };

  return (
    <div className="mx-auto p-4 sm:p-6 md:p-8 lg:ml-[280px] mt-10  h-[80%] overflow-y-scroll">
      <div className="flex justify-between mb-4">
        <h1 className="text-2xl font-bold">Jobs</h1>
        <button
          onClick={openAddModal}
          className="bg-gradient-to-r from-[#FFBE00] to-[#BA5B00] px-4 py-2 text-white rounded-md hover:bg-yellow-600 transition"
        >
          Add Job
        </button>
      </div>

      <table className="w-full border rounded-md shadow-sm">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-3">#</th>
            <th className="p-3">English Title</th>
            <th className="p-3">Chinese Title</th>
            <th className="p-3">Description (EN)</th>
            <th className="p-3">Description (CH)</th>
            <th className="p-3">Image</th>
            <th className="p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" className="text-center p-4">
                Loading...
              </td>
            </tr>
          ) : jobs.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center p-4">
                No Jobs found
              </td>
            </tr>
          ) : (
            jobs.map((job, i) => (
              <tr key={job.id} className="border-t hover:bg-gray-50">
                <td className="p-3">{i + 1}</td>
                <td className="p-3">{job.englishTitle}</td>
                <td className="p-3">{job.chineseTitle}</td>
                <td className="p-3" dangerouslySetInnerHTML={{ __html: job.englishDescription }} />
                <td className="p-3" dangerouslySetInnerHTML={{ __html: job.chineseDescription }} />
                <td className="p-3">
                  {job.image && (
                    <img src={job.image} alt="job" className="w-12 h-12 object-cover rounded" />
                  )}
                </td>
                <td className="p-3">
                  <button onClick={() => openEditModal(job)} className="text-blue-600 hover:underline mr-2">
                    Edit
                  </button>
                  <button onClick={() => openDeleteModal(job.id)} className="text-red-600 hover:underline">
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Add Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  overflow-y-scroll h-[70%] p-6 w-full max-w-2xl rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6">Add Job</h2>
            <div className="grid gap-4">
              <input
                name="englishTitle"
                value={newJob.englishTitle}
                onChange={handleAddInputChange}
                placeholder="English Title"
                className="border p-2 rounded"
              />
              <input
                name="chineseTitle"
                value={newJob.chineseTitle}
                onChange={handleAddInputChange}
                placeholder="Chinese Title"
                className="border p-2 rounded"
              />

              <div>
                <ReactQuill
                  theme="snow"
                  value={newJob.englishDescription}
                  onChange={(content) => setNewJob((prev) => ({ ...prev, englishDescription: content }))}
                  placeholder="English Description"
                  style={quillStyle}
                />
              </div>

              <div>
                <ReactQuill
                  theme="snow"
                  value={newJob.chineseDescription}
                  onChange={(content) => setNewJob((prev) => ({ ...prev, chineseDescription: content }))}
                  placeholder="Chinese Description"
                  style={quillStyle}
                />
              </div>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRefAdd}
                onChange={(e) => handleFileChange(e, false)}
                className="hidden"
              />
              {newJob.image && (
                <div className="relative w-24 h-24">
                  <img src={newJob.image} alt="preview" className="w-24 h-24 object-cover rounded" />
                  <button
                    onClick={() => handleRemoveImage(false)}
                    className="absolute top-0 right-0 bg-red-600 text-white rounded-full w-6 h-6"
                  >
                    ×
                  </button>
                </div>
              )}
              <button
                onClick={() => handleUploadImage(false)}
                disabled={uploadingImage}
                className="bg-yellow-500 text-white px-4 py-2 rounded"
              >
                {uploadingImage ? "Uploading..." : "Upload Image"}
              </button>
            </div>

            <div className="flex gap-4 mt-4">
              <button
                onClick={handleAddJob}
                disabled={addLoading}
                className="bg-green-600 text-white w-full py-2 rounded disabled:opacity-50"
              >
                {addLoading ? "Submitting..." : "Submit"}
              </button>
              <button onClick={closeAllModals} className="bg-gray-300 w-full py-2 rounded">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-full max-w-2xl rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold mb-6">Edit Job</h2>
            <div className="grid gap-4">
              <input
                name="englishTitle"
                value={editingJob.englishTitle}
                onChange={handleEditInputChange}
                placeholder="English Title"
                className="border p-2 rounded"
              />
              <input
                name="chineseTitle"
                value={editingJob.chineseTitle}
                onChange={handleEditInputChange}
                placeholder="Chinese Title"
                className="border p-2 rounded"
              />

              <div>
                <ReactQuill
                  theme="snow"
                  value={editingJob.englishDescription}
                  onChange={(content) =>
                    setEditingJob((prev) => ({ ...prev, englishDescription: content }))
                  }
                  placeholder="English Description"
                  style={quillStyle}
                />
              </div>
              <div>
                <ReactQuill
                  theme="snow"
                  value={editingJob.chineseDescription}
                  onChange={(content) =>
                    setEditingJob((prev) => ({ ...prev, chineseDescription: content }))
                  }
                  placeholder="Chinese Description"
                  style={quillStyle}
                />
              </div>


              <input
                type="file"
                accept="image/*"
                ref={fileInputRefEdit}
                onChange={(e) => handleFileChange(e, true)}
                className="hidden"
              />
              {editingJob.image && (
                <div className="relative w-24 h-24">
                  <img src={editingJob.image} alt="preview" className="w-24 h-24 object-cover rounded" />
                  <button
                    onClick={() => handleRemoveImage(true)}
                    className="absolute top-0 right-0 bg-red-600 text-white rounded-full w-6 h-6"
                  >
                    ×
                  </button>
                </div>
              )}
              <button
                onClick={() => handleUploadImage(true)}
                disabled={uploadingImage}
                className="bg-yellow-500 text-white px-4 py-2 rounded"
              >
                {uploadingImage ? "Uploading..." : "Upload Image"}
              </button>
            </div>

            <div className="flex gap-4 mt-4">
              <button
                onClick={handleUpdateJob}
                disabled={isUpdating}
                className="bg-blue-600 text-white w-full py-2 rounded disabled:opacity-50"
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
              <button onClick={closeAllModals} className="bg-gray-300 w-full py-2 rounded">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-full max-w-md rounded-lg shadow-lg">
            <p className="mb-4 text-lg">Are you sure you want to delete this job?</p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteJob}
                disabled={deleteLoading}
                className="bg-red-600 text-white px-4 py-2 rounded disabled:opacity-50"
              >
                {deleteLoading ? "Deleting..." : "Yes"}
              </button>
              <button onClick={closeAllModals} className="bg-gray-300 px-4 py-2 rounded">
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
